import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import type { ImpactItem, TrustPoint } from "@/lib/types";
import ImageUpload from "@/pages/admin/components/ImageUpload";
import PaymentKeysSection from "./components/PaymentKeysSection";
import type { PaymentKeys } from "./components/PaymentKeysSection";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

const iconOptions = [
  "ri-heart-3-line",
  "ri-restaurant-line",
  "ri-hand-heart-line",
  "ri-group-line",
  "ri-user-heart-line",
  "ri-shield-check-line",
  "ri-flashlight-line",
  "ri-map-pin-line",
  "ri-team-line",
  "ri-book-open-line",
  "ri-first-aid-kit-line",
  "ri-eye-line",
  "ri-file-list-3-line",
  "ri-moon-clear-line",
  "ri-flag-line",
  "ri-chat-smile-3-line",
];

const sectionTitle =
  "font-heading text-base font-semibold text-foreground-950";

export default function AdminDons() {
  const [amounts, setAmounts] = useState<number[]>([]);
  const [trustPoints, setTrustPoints] = useState<TrustPoint[]>([]);
  const [impactItems, setImpactItems] = useState<ImpactItem[]>([]);
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [paypalEnabled, setPaypalEnabled] = useState(true);
  const [payKeys, setPayKeys] = useState<PaymentKeys>({
    mode: "test",
    stripe_test_secret_key: "",
    stripe_live_secret_key: "",
    paypal_test_client_id: "",
    paypal_test_secret: "",
    paypal_live_client_id: "",
    paypal_live_secret: "",
  });
  const [newAmount, setNewAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let active = true;
    supabase
      .from("donation_config")
      .select("*")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || !data) return;
        if (data.amounts && data.amounts.length > 0) setAmounts(data.amounts);
        if (data.trust_points && data.trust_points.length > 0)
          setTrustPoints(data.trust_points);
        if (data.impact_items && data.impact_items.length > 0)
          setImpactItems(data.impact_items);
        if (typeof data.stripe_enabled === "boolean")
          setStripeEnabled(data.stripe_enabled);
        if (typeof data.paypal_enabled === "boolean")
          setPaypalEnabled(data.paypal_enabled);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    supabase
      .from("payment_settings")
      .select("*")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || !data) return;
        setPayKeys({
          mode: data.mode === "live" ? "live" : "test",
          stripe_test_secret_key: data.stripe_test_secret_key ?? "",
          stripe_live_secret_key: data.stripe_live_secret_key ?? "",
          paypal_test_client_id: data.paypal_test_client_id ?? "",
          paypal_test_secret: data.paypal_test_secret ?? "",
          paypal_live_client_id: data.paypal_live_client_id ?? "",
          paypal_live_secret: data.paypal_live_secret ?? "",
        });
      });

    return () => {
      active = false;
    };
  }, []);

  function addAmount() {
    const n = Number(newAmount);
    if (!Number.isFinite(n) || n <= 0) return;
    if (amounts.includes(n)) {
      setNewAmount("");
      return;
    }
    setAmounts([...amounts, n].sort((a, b) => a - b));
    setNewAmount("");
  }

  function updateTrust(index: number, patch: Partial<TrustPoint>) {
    setTrustPoints((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p))
    );
  }

  function updateImpact(index: number, patch: Partial<ImpactItem>) {
    setImpactItems((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p))
    );
  }

  const paypalConnected = Boolean(
    payKeys.paypal_test_client_id ||
      payKeys.paypal_live_client_id ||
      import.meta.env.VITE_PUBLIC_PAYPAL_CLIENT_ID
  );
  const stripeConnected = Boolean(
    payKeys.stripe_test_secret_key ||
      payKeys.stripe_live_secret_key ||
      import.meta.env.VITE_PUBLIC_STRIPE_PUBLISHABLE_KEY
  );

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setIsError(false);

    const { error } = await supabase.from("donation_config").upsert(
      {
        id: 1,
        amounts,
        trust_points: trustPoints,
        impact_items: impactItems,
        stripe_enabled: stripeEnabled,
        paypal_enabled: paypalEnabled,
      },
      { onConflict: "id" }
    );

    const { error: payErr } = await supabase
      .from("payment_settings")
      .upsert({ id: 1, ...payKeys }, { onConflict: "id" });

    setSaving(false);
    if (error || payErr) {
      setMsg(error?.message || payErr?.message || "Erreur d'enregistrement.");
      setIsError(true);
      return;
    }
    setMsg("Réglages de la page don enregistrés.");
  }

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[50vh]">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Réglages de la page don
      </h1>
      <p className="mt-2 text-sm md:text-base text-foreground-600">
        Les montants suggérés, les points de confiance et les blocs d'impact
        affichés sur la page « Faire un don ».
      </p>

      {msg && (
        <div
          className={`mt-6 rounded-md px-4 py-3 text-sm ${
            isError ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"
          }`}
        >
          {msg}
        </div>
      )}

      <form
        onSubmit={handleSave}
        className="mt-6 space-y-6"
      >
        <section className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
          <h2 className={sectionTitle}>Moyens de paiement</h2>
          <p className="mt-1 text-sm text-foreground-600">
            Activez ou désactivez les moyens de paiement proposés sur les
            cagnottes.
          </p>

          <div className="mt-4 rounded-md bg-secondary-100 text-secondary-900 px-4 py-3 text-sm flex items-start gap-2">
            <i className="ri-information-line mt-0.5"></i>
            <span>
              Vous pouvez connecter vos comptes Stripe et PayPal via
              l'assistant, ou saisir manuellement vos clés API dans la section
              « Clés API & environnement » ci-dessous.
            </span>
          </div>

          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between gap-4 rounded-md border border-background-200 p-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-bank-card-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-800">
                    Paiement par carte (Stripe)
                  </p>
                  <p className="text-xs text-foreground-500">
                    {stripeConnected
                      ? "Compte connecté"
                      : "Non connecté — connectez depuis l'assistant"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStripeEnabled(!stripeEnabled)}
                role="switch"
                aria-checked={stripeEnabled}
                aria-label="Activer le paiement par carte"
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
                  stripeEnabled ? "bg-primary-500" : "bg-background-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background-50 transition-transform ${
                    stripeEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border border-background-200 p-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-paypal-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-800">
                    PayPal
                  </p>
                  <p className="text-xs text-foreground-500">
                    {paypalConnected
                      ? "Compte connecté"
                      : "Non connecté — connectez depuis l'assistant"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPaypalEnabled(!paypalEnabled)}
                role="switch"
                aria-checked={paypalEnabled}
                aria-label="Activer PayPal"
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
                  paypalEnabled ? "bg-primary-500" : "bg-background-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background-50 transition-transform ${
                    paypalEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        <PaymentKeysSection
          value={payKeys}
          onChange={(patch) => setPayKeys((prev) => ({ ...prev, ...patch }))}
        />

        <section className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
          <h2 className={sectionTitle}>Montants suggérés</h2>
          <p className="mt-1 text-sm text-foreground-600">
            Les boutons de montant affichés dans le formulaire de don.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {amounts.map((amount) => (
              <span
                key={amount}
                className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm font-semibold text-foreground-800"
              >
                {amount} €
                <button
                  type="button"
                  onClick={() => setAmounts(amounts.filter((a) => a !== amount))}
                  aria-label={`Retirer ${amount} €`}
                  className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-500 hover:text-primary-600 hover:bg-primary-100 transition cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </span>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-2 max-w-xs">
            <input
              type="number"
              min="1"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addAmount();
                }
              }}
              placeholder="Ajouter un montant"
              className={inputClass}
            />
            <button
              type="button"
              onClick={addAmount}
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full bg-secondary-500 text-background-50 hover:bg-secondary-600 px-4 py-2.5 text-sm font-semibold transition cursor-pointer"
            >
              <i className="ri-add-line"></i>
              Ajouter
            </button>
          </div>
        </section>

        <section className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
          <h2 className={sectionTitle}>Points de confiance</h2>
          <p className="mt-1 text-sm text-foreground-600">
            Les arguments de réassurance listés à côté du formulaire.
          </p>

          <div className="mt-5 space-y-4">
            {trustPoints.map((point, index) => (
              <div
                key={index}
                className="rounded-md border border-background-200 p-4 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                      <i className={`${point.icon} text-lg`}></i>
                    </span>
                    <span className="text-sm font-semibold text-foreground-700">
                      Point {index + 1}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setTrustPoints(trustPoints.filter((_, i) => i !== index))
                    }
                    aria-label="Supprimer ce point"
                    className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={point.title}
                    onChange={(e) => updateTrust(index, { title: e.target.value })}
                    placeholder="Titre"
                    className={inputClass}
                  />
                  <select
                    value={point.icon}
                    onChange={(e) => updateTrust(index, { icon: e.target.value })}
                    className={inputClass}
                  >
                    {iconOptions.map((icon) => (
                      <option key={icon} value={icon}>
                        {icon.replace("ri-", "").replace("-line", "")}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  rows={2}
                  value={point.description}
                  onChange={(e) =>
                    updateTrust(index, { description: e.target.value })
                  }
                  placeholder="Description"
                  className={inputClass}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() =>
              setTrustPoints([
                ...trustPoints,
                { title: "", description: "", icon: "ri-shield-check-line" },
              ])
            }
            className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 px-5 py-2.5 text-sm font-semibold transition cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Ajouter un point de confiance
          </button>
        </section>

        <section className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
          <h2 className={sectionTitle}>Blocs d'impact</h2>
          <p className="mt-1 text-sm text-foreground-600">
            Les trois cartes « Votre don en action » de la page don.
          </p>

          <div className="mt-5 space-y-4">
            {impactItems.map((item, index) => (
              <div
                key={index}
                className="rounded-md border border-background-200 p-4 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {item.image ? (
                      <div className="w-10 h-10 rounded-md overflow-hidden border border-background-200">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    ) : (
                      <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                    )}
                    <span className="text-sm font-semibold text-foreground-700">
                      Bloc {index + 1}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setImpactItems(impactItems.filter((_, i) => i !== index))
                    }
                    aria-label="Supprimer ce bloc"
                    className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => updateImpact(index, { title: e.target.value })}
                    placeholder="Titre"
                    className={inputClass}
                  />
                  <select
                    value={item.icon}
                    onChange={(e) => updateImpact(index, { icon: e.target.value })}
                    className={inputClass}
                  >
                    {iconOptions.map((icon) => (
                      <option key={icon} value={icon}>
                        {icon.replace("ri-", "").replace("-line", "")}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  rows={2}
                  value={item.description}
                  onChange={(e) =>
                    updateImpact(index, { description: e.target.value })
                  }
                  placeholder="Description"
                  className={inputClass}
                />
                <ImageUpload
                  value={item.image}
                  onChange={(url) => updateImpact(index, { image: url })}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() =>
              setImpactItems([
                ...impactItems,
                { title: "", description: "", icon: "ri-heart-3-line", image: "" },
              ])
            }
            className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 px-5 py-2.5 text-sm font-semibold transition cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Ajouter un bloc d'impact
          </button>
        </section>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-8 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Enregistrement…
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Enregistrer les réglages
            </>
          )}
        </button>
      </form>
    </div>
  );
}